import type { ReactNode } from "react";
import { useEntLinkContext } from "@/contexts/EntLinkContext.tsx";
import { getAdminUrl } from "@/lib/utils.ts";

const URN_PATTERN = /urn:bb:[a-zA-Z][a-zA-Z0-9]*:[a-zA-Z0-9]+/g;

/**
 * Renders text with URN patterns converted to clickable links.
 * If EntLinkContext is provided, clicking opens a modal. Otherwise, navigates to admin graph.
 */
export const EntLinkifiedText = ({ text }: { text?: string }) => {
  const entLinkContext = useEntLinkContext();

  if (!text) {
    return null;
  }

  const matches = [...text.matchAll(URN_PATTERN)];

  if (matches.length === 0) {
    return <span>{text}</span>;
  }

  const parts: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    const urn = match[0];
    const startIndex = match.index!;
    const entId = urn.split(":").at(-1)!;

    // Add text before the URN
    if (startIndex > lastIndex) {
      parts.push(text.slice(lastIndex, startIndex));
    }

    // Add the URN as a link or button depending on context
    if (entLinkContext) {
      // Modal behavior - render as button
      parts.push(
        <button
          key={startIndex}
          type="button"
          onClick={() => entLinkContext.onEntClick(entId)}
          className="link font-mono inline"
        >
          {urn}
        </button>,
      );
    } else {
      // Navigation behavior - link to admin service
      parts.push(
        <a
          key={startIndex}
          href={`${getAdminUrl()}/admin/graph/ent/${entId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="link font-mono inline"
        >
          {urn}
        </a>,
      );
    }

    lastIndex = startIndex + urn.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <span>{parts}</span>;
};
